import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import { CHART_COLORS } from '../../lib/chart-theme';

interface BarSeries {
    name: string;
    data: number[];
    color?: string;
    stack?: string;
}

interface ReportBarChartProps {
    title: string;
    categories: string[];
    series: BarSeries[];
    loading?: boolean;
    height?: number;
    valueFormatter?: (value: number) => string;
    onBarClick?: (categoryIndex: number, category: string) => void;
    tooltipShowStackTotal?: boolean;
}

export function ReportBarChart({ title, categories, series, loading, height = 400, valueFormatter, onBarClick, tooltipShowStackTotal = false }: ReportBarChartProps) {
    const fmt = valueFormatter ?? ((v: number) => v.toLocaleString('fr-FR'));

    // Tooltip: en mode total cumulé, on reconstruit le HTML pour ajouter une ligne « Total ».
    // Les séries masquées via la légende ne sont pas présentes dans `params` (comportement natif ECharts en trigger axis).
    // `confine` empêche le tooltip de déborder hors du graphe ; `enterable` + `extraCssText`
    // permettent le scroll à la souris quand la liste de séries est plus haute que 320 px.
    const tooltipBase = {
        confine: true,
        enterable: true,
        extraCssText: 'max-height:320px;overflow-y:auto;overflow-x:hidden;',
    };

    const tooltip: EChartsOption['tooltip'] = tooltipShowStackTotal
        ? {
              ...tooltipBase,
              trigger: 'axis',
              formatter: (params: any) => {
                  const rows = Array.isArray(params) ? params : [params];
                  if (rows.length === 0) return '';
                  const header = rows[0].axisValueLabel ?? rows[0].axisValue ?? '';
                  const lines = rows
                      .filter((p: any) => p.value !== undefined && p.value !== null && p.value !== '')
                      .map(
                          (p: any) =>
                              `<div style="display:flex;justify-content:space-between;gap:16px;line-height:1.6">`
                              + `<span>${p.marker} ${p.seriesName}</span>`
                              + `<span style="font-weight:600">${fmt(Number(p.value) || 0)}</span>`
                              + `</div>`,
                      )
                      .join('');
                  const total = rows.reduce((s: number, p: any) => s + (Number(p.value) || 0), 0);
                  const totalLine =
                      `<div style="display:flex;justify-content:space-between;gap:16px;line-height:1.6;border-top:1px solid #e5e7eb;margin-top:4px;padding-top:4px">`
                      + `<span style="font-weight:600">Total</span>`
                      + `<span style="font-weight:700">${fmt(total)}</span>`
                      + `</div>`;
                  return `<div style="font-weight:600;margin-bottom:4px">${header}</div>${lines}${totalLine}`;
              },
          }
        : {
              ...tooltipBase,
              trigger: 'axis',
              valueFormatter: (value) => fmt(Number(value)),
          };

    const option: EChartsOption = {
        tooltip,
        legend: { bottom: 0, type: 'scroll' },
        grid: { left: '8%', right: '4%', bottom: '12%', top: '8%', containLabel: true },
        xAxis: {
            type: 'category',
            data: categories,
            axisLabel: { rotate: categories.length > 6 ? 45 : 0, fontSize: 11 },
        },
        yAxis: {
            type: 'value',
            axisLabel: { formatter: (v: number) => fmt(v) },
        },
        series: series.map((s, i) => ({
            name: s.name,
            type: 'bar' as const,
            data: s.data,
            stack: s.stack,
            itemStyle: { color: s.color ?? CHART_COLORS[i % CHART_COLORS.length] },
        })),
    };

    const onEvents = onBarClick
        ? {
              click: (params: any) => {
                  onBarClick(params.dataIndex, categories[params.dataIndex]);
              },
          }
        : undefined;

    return (
        <div>
            <h4 className="text-sm font-medium text-gray-700 text-center mb-1">{title}</h4>
            <ReactECharts
                option={option}
                style={{ height: `${height}px` }}
                showLoading={loading}
                onEvents={onEvents}
            />
        </div>
    );
}
